import { useCallback, useMemo } from "react";
import {
  ApolloClient,
  ApolloError,
  ApolloLink,
  InMemoryCache,
  useLazyQuery as useApolloLazyQuery,
} from "@apollo/client";

import { useMaybeAuth } from "auth/AuthContext";
import { Query } from "base-types";
import { useAppVersion } from "contexts/AppVersion/AppVersionContext";
import { useSyncRef } from "hooks/useSyncRef";
import { GraphQLClient } from "types";

import {
  notifyGraphQLError,
  parseApolloError,
  ParsedGraphQLError,
} from "./errors";
import { RequestContextOptions } from "./request-context-utils";
import { ErrorHandler, SuccessCallback, WithOperation } from "./types";
import { useUnauthSchemaHttpLink } from "./useUnauthSchemaHttpLink";
import { getOutput } from "./utils";

export type LazyUnauthQueryOptions<
  Data,
  Schema extends
    | "UNAUTHENTICATED_COPILOT_API_DEVELOPER"
    | "UNAUTHENTICATED_ACCOUNT",
> = RequestContextOptions<Schema> & {
  onSuccess?: SuccessCallback<Data>;
  onError?: ErrorHandler;
};

export const useLazyUnauthQuery = <
  Data,
  Variables,
  VariablesRequired,
  Schema extends
    | "UNAUTHENTICATED_COPILOT_API_DEVELOPER"
    | "UNAUTHENTICATED_ACCOUNT",
>(
  query: Query<Data, Variables, VariablesRequired, Schema>,

  ...options: Partial<
    LazyUnauthQueryOptions<Data, Schema>
  > extends LazyUnauthQueryOptions<Data, Schema>
    ? [options?: LazyUnauthQueryOptions<Data, Schema>]
    : [options: LazyUnauthQueryOptions<Data, Schema>]
): [
  (variables?: Variables) => Promise<Data | undefined>,
  {
    data: Data | undefined;
    error: ParsedGraphQLError | undefined;
    loading: boolean;
  },
] => {
  const { onSuccess, onError, requestContext } = options[0] ?? {};

  const httpLink = useUnauthSchemaHttpLink(query.schemaType);
  const optionsRef = useSyncRef({ onSuccess, onError });
  const { state: appVersionState } = useAppVersion();
  const auth = useMaybeAuth();

  const client = useMemo(() => {
    const cache = new InMemoryCache();
    return new ApolloClient({
      link: new ApolloLink(httpLink),
      cache,
    }) as GraphQLClient;
  }, [httpLink]);

  const apolloContext = {
    requestContext,
    // Disable query deduplication as context might differ
    queryDeduplication: false,
  };

  const [execute, { data, error, loading }] = useApolloLazyQuery<
    WithOperation<Data>,
    Variables
  >(query.document, {
    client,
    fetchPolicy: "no-cache",
    context: apolloContext,
    onCompleted: useCallback(
      (rawData: WithOperation<Data>) => {
        const output = getOutput(rawData);
        if (output) optionsRef.current.onSuccess?.(output, client);
      },
      [optionsRef, client],
    ),
    onError: useCallback(
      (e: ApolloError) => {
        const parsedError = parseApolloError(e);
        optionsRef.current.onError
          ? optionsRef.current.onError(parsedError, () =>
              notifyGraphQLError(appVersionState, auth?.state, e),
            )
          : notifyGraphQLError(appVersionState, auth?.state, e);
      },
      [optionsRef, auth?.state, appVersionState],
    ),
  });

  return [
    useCallback(
      (variables: Variables | undefined) =>
        execute({ variables })
          .then((result) => getOutput(result.data))
          .catch((e: ApolloError) => {
            throw parseApolloError(e);
          }),
      [execute],
    ),
    {
      data: getOutput(data),
      error: error && parseApolloError(error),
      loading,
    },
  ];
};
